import React from 'react';
import FieldTypesEnum from '../Enums/FieldTypesEnum';
import dateTimeHelper from '../Helpers/datetimeHelper';
import moneyHelper from '../Helpers/moneyHelper';
import DataTableActionMenu from '../Elements/DataTable/DataTableActionMenu';
import OutsourcedStatusEnum from '../Enums/OutsourcedStatusEnum';
import OutsourcedSourceTypeEnum, { OutsourcedSourceTypeLabels } from '../Enums/OutsourcedSourceTypeEnum';
import AuthService from '../Services/authService';

export default class OutsourcedIOBLineItemDetailItem {
  constructor(item) {
    if (item === undefined) {
      this.OutsourcedIOBLineItemDetailId = null;
      this.OutsourcedUserId = null;
      this.OutsourcedUserName = '';
      this.TimesheetEntryId = null;
      this.ClientId = null;
      this.ClientName = '';
      this.ThirdPartyClientId = '';
      this.SassieClientId = '';
      this.ThirdPartySource = '';
      this.ClientFeeId = null;
      this.ClientFeeName = '';
      this.ProjectId = null;
      this.ProjectName = '';
      this.SurveyId = null;
      this.SurveyName = '';
      this.SassieSurveyId = '';
      this.TpSurveyId = null;
      this.TpSurveyName = '';
      this.ThirdPartySurveyId = '';
      this.WaveId = null;
      this.WaveName = '';
      this.SassieWaveId = null;
      this.TpWaveId = null;
      this.TpWaveName = '';
      this.ThirdPartyWaveId = '';
      // Seeded, not null: CaspianDatePicker displays today when its value is null but never reports it, so a
      // null leaves this required field looking filled while validation calls it empty and Finish stays disabled.
      this.ApplyDate = new Date();
      this.OutsourcingOfficeId = null;
      this.OutsourcingOfficeName = '';
      this.FieldworkOfficeId = null;
      this.FieldworkOfficeName = '';
      this.FeeAmount = null;
      this.CurrencyCode = '';
      this.IobInvoiceId = null;
      this.LineItemDetailType = 'Addition';
      this.SourceType = OutsourcedSourceTypeEnum.Manual;
      this.SourceTypeName = OutsourcedSourceTypeLabels[OutsourcedSourceTypeEnum.Manual];
      this.IsManual = true;
    } else {
      this.initialize(item);
    }
  }

  initialize(item) {
    this.OutsourcedIOBLineItemDetailId = item.outsourcedIOBLineItemDetailId;
    this.OutsourcedUserId = item.outsourcedUserId;
    this.OutsourcedUserName = item.outsourcedUserName ?? '';
    this.TimesheetEntryId = item.timesheetEntryId;
    this.ClientId = item.clientId;
    this.ClientName = item.clientName ?? '';
    this.ThirdPartyClientId = item.thirdPartyClientId ?? '';
    this.SassieClientId = item.sassieClientId ?? '';
    this.ThirdPartySource = item.thirdPartySource ?? '';
    this.ClientFeeId = item.clientFeeId;
    this.ClientFeeName = item.clientFeeName ?? '';
    this.ProjectId = item.projectId;
    this.ProjectName = item.projectName ?? '';
    this.SurveyId = item.surveyId;
    this.SurveyName = item.surveyName ?? '';
    this.SassieSurveyId = item.sassieSurveyId ?? '';
    this.TpSurveyId = item.tpSurveyId;
    this.TpSurveyName = item.tpSurveyName ?? '';
    this.ThirdPartySurveyId = item.thirdPartySurveyId ?? '';
    this.WaveId = item.waveId;
    this.WaveName = item.waveName ?? '';
    this.SassieWaveId = item.sassieWaveId ?? '';
    this.TpWaveId = item.tpWaveId;
    this.TpWaveName = item.tpWaveName ?? '';
    this.ThirdPartyWaveId = item.thirdPartyWaveId ?? '';
    this.ApplyDate = item.applyDate;
    this.OutsourcingOfficeId = item.outsourcingOfficeId;
    this.OutsourcingOfficeName = (item.outsourcingOfficeName ?? '').replace('BARE ', '').trim();
    this.FieldworkOfficeId = item.fieldworkOfficeId;
    this.FieldworkOfficeName = (item.fieldworkOfficeName ?? '').replace('BARE ', '').trim();
    this.FeeAmount = item.feeAmount;
    this.CurrencyCode = item.currencyCode ?? '';
    this.Status = item.status ?? '';
    this.IobInvoiceId = item.iobInvoiceId;
    this.LineItemDetailType = item.lineItemDetailType ?? '';
    this.SourceType = item.sourceType ?? '';
    this.SourceTypeName = OutsourcedSourceTypeLabels[item.sourceType] ?? item.sourceType ?? '';
    this.DisplayedStatus =
      item.status === OutsourcedStatusEnum.Collected && (item.isManual ?? false) ? 'Created' : item.status ?? '';
    this.IsManual = item.isManual ?? false;
    this.Selected = false;
  }

  static NameOfTheIdField() {
    return 'OutsourcedIOBLineItemDetailId';
  }

  static nameOfTheItemDetailList(item) {
    // No id in the caption: this row's is a GUID, which means nothing to a user (CD-5079).
    return 'Outsourced IOB Line Item Detail';
  }

  static MoneyFields = ['FeeAmount'];

  static Currency() {
    return 'CurrencyCode';
  }

  static MoneyFieldsSecondary = [];

  static CurrencySecondary() {
    return '';
  }

  static DateFields = ['ApplyDate'];

  static BoolFields = [];

  static FieldsToShowInListTable = [
    {
      tabName: 'General',
      tabLabel: 'General',
      icon: 'fa-solid fa-home',
      data: [
        { name: '-message-', label: 'General Information', className: 'text-left' },

        // Work Done By before Bill To Office, as on every other outsourced surface (CD-5079).
        { name: 'OutsourcingOfficeName', label: 'Work Done By' },
        { name: 'FieldworkOfficeName', label: 'Bill To Office' },

        {
          name: (item) => moneyHelper.toMoneyFormat(item.FeeAmount, item.CurrencyCode),
          label: 'Fee',
        },
        { name: 'LineItemDetailType', label: 'Transaction Type' },
        // "Origin", not "Source" - the Third Party Details block below already uses "Source" for the
        // third-party system (Apollo/Prism).
        { name: 'SourceTypeName', label: 'Origin' },

        { name: 'OutsourcedUserName', label: 'Outsourced User' },
        { name: 'OutsourcedUserId', label: 'User ID' },
        { name: (item) => dateTimeHelper.toYYYYsMMsDD(item.ApplyDate), label: 'Apply Date' },

        { name: 'IobInvoiceId', label: 'IOB Invoice ID' },
        { name: 'TimesheetEntryId', label: 'Timesheet Entry ID' },

        { name: '-message-', label: 'Third Party Details', className: 'text-left' },

        // Every id is paired with its Bravo counterpart here, because the grid columns show only the Bravo id
        // now - this panel is where someone goes to find the Apollo or third-party one (CD-5079).
        { name: 'ThirdPartySource', label: 'Source' },
        { name: 'ClientName', label: 'Client Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'ClientId', label: 'Client Bravo ID' },
        { name: 'SassieClientId', label: 'Apollo Client ID' },
        { name: 'ThirdPartyClientId', label: 'Third Party Client ID' },

        { name: 'ClientFeeName', label: 'Project (Apollo)' },
        { name: 'ProjectName', label: 'Project' },
        { label: '', name: '', className: 'text-left' },

        { name: 'ClientFeeId', label: 'Project (Apollo) Bravo ID' },
        { name: 'ProjectId', label: 'Project Bravo ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'SurveyName', label: 'Survey Name' },
        { name: 'TpSurveyName', label: 'TP Survey Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'SurveyId', label: 'Survey Bravo ID' },
        { name: 'SassieSurveyId', label: 'Apollo Survey ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'TpSurveyId', label: 'TP Survey Bravo ID' },
        { name: 'ThirdPartySurveyId', label: 'Third Party Survey ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'WaveName', label: 'Wave Name' },
        { name: 'TpWaveName', label: 'TP Wave Name' },
        { label: '', name: '', className: 'text-left' },

        { name: 'WaveId', label: 'Wave Bravo ID' },
        { name: 'SassieWaveId', label: 'Apollo Wave ID' },
        { label: '', name: '', className: 'text-left' },

        { name: 'TpWaveId', label: 'TP Wave Bravo ID' },
        { name: 'ThirdPartyWaveId', label: 'Third Party Wave ID' },
        { label: '', name: '', className: 'text-left' },
      ],
    },
  ];

  static getUserDisplay(item) {
    return item.OutsourcedUserId ? `${item.OutsourcedUserName} (${item.OutsourcedUserId})` : item.OutsourcedUserName;
  }

  // See OutsourcedFeeItem: the id in brackets is always the BRAVO id now, not the third-party one users were
  // mistaking it for (CD-5079). The Apollo and third-party ids are on the details panel, each labelled.
  static getClientDisplay(item) {
    return item.ClientId ? `${item.ClientName} (${item.ClientId})` : item.ClientName;
  }

  static getSurveyDisplay(item) {
    if (item.TpSurveyId) {
      return `${item.TpSurveyName} (${item.TpSurveyId})`;
    }
    if (item.SurveyId) {
      return `${item.SurveyName} (${item.SurveyId})`;
    }
    return '';
  }

  static getFeeDisplay(item) {
    return item.FeeAmount != null ? moneyHelper.toMoneyFormat(item.FeeAmount, item.CurrencyCode) : '';
  }

  static FieldsToShowInDataTable() {
    return [
      {
        label: OutsourcedIOBLineItemDetailItem.getSelectedCellHeader,
        orderById: '',
        data: OutsourcedIOBLineItemDetailItem.getSelectedCellContent,
        type: FieldTypesEnum.PureDataField,
        minWidth: 10,
      },
      {
        // See OutsourcedFeeItem: the details link sits on the row's position, not on the GUID and not
        // on a business column (user, client, office, dates all repeat across rows), because the link
        // column has to be unique on screen. No orderById: a positional counter is not sortable.
        label: 'Page Position',
        orderById: '',
        data: 'PagePosition',
        type: FieldTypesEnum.DataFieldWithDetails,
        minWidth: 50,
      },
      {
        label: 'Outsourced User (Bravo ID)',
        orderById: 'OutsourcedUserName',
        data: OutsourcedIOBLineItemDetailItem.getUserDisplay,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Client Name (Bravo ID)',
        orderById: 'ClientName',
        data: OutsourcedIOBLineItemDetailItem.getClientDisplay,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Source',
        orderById: 'ThirdPartySource',
        data: 'ThirdPartySource',
        type: FieldTypesEnum.PureDataField,
        minWidth: 60,
      },
      {
        label: 'Survey (Bravo ID)',
        orderById: 'SurveyName',
        data: OutsourcedIOBLineItemDetailItem.getSurveyDisplay,
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Apply Date',
        orderById: 'ApplyDate',
        data: (item) => dateTimeHelper.toYYYYsMMsDD(item.ApplyDate),
        type: FieldTypesEnum.PureDataField,
        minWidth: 80,
      },
      {
        // Work Done By before Bill To Office, as on every other outsourced surface (CD-5079).
        label: 'Work Done By',
        orderById: 'OutsourcingOfficeName',
        data: 'OutsourcingOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Bill To Office',
        orderById: 'FieldworkOfficeName',
        data: 'FieldworkOfficeName',
        type: FieldTypesEnum.PureDataField,
      },
      {
        label: 'Fee',
        orderById: 'FeeAmount',
        data: OutsourcedIOBLineItemDetailItem.getFeeDisplay,
        type: FieldTypesEnum.PureDataField,
        minWidth: 60,
      },
      {
        label: 'IOB Invoice ID',
        orderById: 'IobInvoiceId',
        data: 'IobInvoiceId',
        type: FieldTypesEnum.PureDataField,
        minWidth: 60,
      },
      {
        label: 'Transaction Type',
        orderById: 'LineItemDetailType',
        data: 'LineItemDetailType',
        type: FieldTypesEnum.PureDataField,
        minWidth: 80,
      },
      {
        label: 'Origin',
        orderById: 'SourceType',
        data: 'SourceTypeName',
        type: FieldTypesEnum.PureDataField,
        minWidth: 70,
      },
      {
        label: 'Status',
        orderById: 'Status',
        data: 'DisplayedStatus',
        type: FieldTypesEnum.PureDataField,
        minWidth: 50,
      },
      {
        label: ' ',
        orderById: '',
        data: OutsourcedIOBLineItemDetailItem.getActionsContent,
        type: FieldTypesEnum.ActionButton,
        className: 'text-center',
      },
    ];
  }

  // Rows created by the Outsourced FTE flows are the breakdown of an IOB that already exists: they are
  // born Invoiced and take no part in the approval lifecycle, so every action is closed on them.
  static isFteOwned(item) {
    return (
      item.SourceType === OutsourcedSourceTypeEnum.OutsourcedFte ||
      item.SourceType === OutsourcedSourceTypeEnum.ReverseFte
    );
  }

  static getActionsContent(item) {
    const userOfficeId = AuthService.getInstance().GetLoggedInUser()?.officeId;
    const fteOwned = OutsourcedIOBLineItemDetailItem.isFteOwned(item);
    return (
      <DataTableActionMenu
        options={[
          {
            iconName: 'fa-solid fa-redo',
            text: 'Repeat',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            disabled: !item.IsManual,
            onClick: () => OutsourcedIOBLineItemDetailItem.repeatFunction(item.OutsourcedIOBLineItemDetailId, item),
          },
          {
            iconName: 'fa-solid fa-edit',
            text: 'Edit',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            disabled: fteOwned || item.Status !== OutsourcedStatusEnum.Collected,
            onClick: () => OutsourcedIOBLineItemDetailItem.editFunction(item.OutsourcedIOBLineItemDetailId, item),
          },
          {
            iconName: 'fa-solid fa-ban',
            text: 'Mark as Uninvoiceable',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            hide: fteOwned || item.Status === OutsourcedStatusEnum.Uninvoiceable,
            disabled: item.Status === OutsourcedStatusEnum.Invoiced,
            onClick: () =>
              OutsourcedIOBLineItemDetailItem.markAsUninvoiceableFunction(item.OutsourcedIOBLineItemDetailId, item),
          },
          {
            iconName: 'fa-solid fa-check-circle',
            text: 'Mark as Invoiceable',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            hide: fteOwned || item.Status !== OutsourcedStatusEnum.Uninvoiceable,
            onClick: () =>
              OutsourcedIOBLineItemDetailItem.markAsInvoiceableFunction(item.OutsourcedIOBLineItemDetailId, item),
          },
          {
            iconName: 'fa-solid fa-check',
            text: 'Approve (Bill To Office)',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            disabled:
              fteOwned || item.Status !== OutsourcedStatusEnum.Collected || item.FieldworkOfficeId !== userOfficeId,
            onClick: () => OutsourcedIOBLineItemDetailItem.fwApproveFunction(item.OutsourcedIOBLineItemDetailId),
          },
          {
            iconName: 'fa-solid fa-check-double',
            text: 'Approve (Work Done By)',
            roleKey: 'outsourced_ioblineitemdetails_edit',
            disabled:
              fteOwned ||
              item.Status !== OutsourcedStatusEnum.ReadyForInvoicing ||
              item.OutsourcingOfficeId !== userOfficeId,
            onClick: () => OutsourcedIOBLineItemDetailItem.osApproveFunction(item.OutsourcedIOBLineItemDetailId),
          },
        ]}
      />
    );
  }

  static markAsUninvoiceableFunction = (id, item) => {
    alert('Empty function');
  };

  static markAsInvoiceableFunction = (id, item) => {
    alert('Empty function');
  };

  static fwApproveFunction = (id) => {
    alert('Empty function');
  };

  static osApproveFunction = (id) => {
    alert('Empty function');
  };

  static editFunction = (id, item) => {
    alert('Empty function');
  };

  static repeatFunction = (id, item) => {
    alert('Empty function');
  };

  static clickOnGlobalSelect(item, selected) {
    // Placeholder, don't remove it!
  }

  static clickOnSelect(item, selected) {
    // Placeholder, don't remove it!
  }

  static selectedStatus() {
    return false;
  }

  static getSelectedCellContent(item) {
    return (
      <input
        type='checkbox'
        checked={item.Selected}
        onChange={(e) => {
          OutsourcedIOBLineItemDetailItem.clickOnSelect(item, e.target.checked);
        }}
      />
    );
  }

  static getSelectedCellHeader(item) {
    return (
      <input
        type='checkbox'
        checked={OutsourcedIOBLineItemDetailItem.selectedStatus()}
        onChange={(e) => {
          OutsourcedIOBLineItemDetailItem.clickOnGlobalSelect(item, e.target.checked);
        }}
      />
    );
  }
}
